import { useState } from 'react';
import { Download, FileCode } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@reflex/ui/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { Button } from '@reflex/ui/components/ui/button';
import { CodeBlock } from '@/components/content/CodeBlock';

/**
 * Link-styled trigger plus a dialog that renders the base image as one
 * standalone, runnable Dockerfile (the server-composed
 * `BaseImageView.dockerfileExample`): the framing dockerfile, every merged
 * setup command as a RUN line, and the file mounts as COPY heredocs.
 */
export function BaseDockerfileDialog({ dockerfile }: { dockerfile: string }) {
  const [open, setOpen] = useState(false);

  function handleDownload() {
    const url = URL.createObjectURL(new Blob([dockerfile], { type: 'text/plain' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'Dockerfile';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          size="sm"
          className="h-auto gap-1 p-0 text-xs"
          aria-label="View base image as Dockerfile"
        >
          <FileCode className="h-3.5 w-3.5" />
          View as runnable Dockerfile
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Base image as a Dockerfile</DialogTitle>
          <DialogDescription>
            The full base image (framing Dockerfile, merged setup commands, and file mounts)
            rendered as one standalone Dockerfile. Build it locally with{' '}
            <span className="font-mono text-foreground/90">docker build - &lt; Dockerfile</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CodeBlock
            title="Dockerfile"
            maxHeight="none"
            headerActions={
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Download Dockerfile"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Download</TooltipContent>
              </Tooltip>
            }
          >
            {dockerfile}
          </CodeBlock>
        </div>
      </DialogContent>
    </Dialog>
  );
}
